import React, {memo, useCallback} from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useBottomInset} from '../../helpers/safeArea';
import {useTheme} from '../../helpers/ThemeContext';
import {POS_FAB_SLOT_WIDTH, POS_TAB_BAR_HEIGHT, splitPosTabRoutes} from '../../helpers/posLayout';

/**
 * Resolves the visible label of a tab: `tabBarLabel` (string) wins, then the
 * screen `title`, then the route name.
 *
 * @param {object} options Descriptor options of the route.
 * @param {string} routeName Route name fallback.
 * @returns {string}
 */
const resolveLabel = (options, routeName) => {
    if (typeof options?.tabBarLabel === 'string') {
        return options.tabBarLabel;
    }

    return options?.title || routeName;
};

/**
 * One big, thumb-friendly tab: icon in a pill, label wrapping onto two lines
 * instead of being cut off with an ellipsis, red count badge.
 */
const PosTab = memo(({route, descriptor, focused, navigation, colors}) => {
    const {options} = descriptor;
    const label = resolveLabel(options, route.name);
    const tint = focused ? colors.primary : colors.subText;
    const badge = options.tabBarBadge;
    const hasBadge = badge != null && badge !== 0 && badge !== '';

    const onPress = useCallback(() => {
        const event = navigation.emit({type: 'tabPress', target: route.key, canPreventDefault: true});

        if (!focused && !event.defaultPrevented) {
            navigation.navigate(route.name, route.params);
        }
    }, [focused, navigation, route.key, route.name, route.params]);

    const onLongPress = useCallback(() => {
        navigation.emit({type: 'tabLongPress', target: route.key});
    }, [navigation, route.key]);

    return (
        <TouchableOpacity
            accessibilityRole="tab"
            accessibilityState={{selected: focused}}
            accessibilityLabel={label}
            activeOpacity={0.7}
            onPress={onPress}
            onLongPress={onLongPress}
            style={styles.tab}
        >
            <View style={[styles.iconPill, focused && {backgroundColor: colors.pill}]}>
                {typeof options.tabBarIcon === 'function' && options.tabBarIcon({focused, color: tint, size: 24})}
                {hasBadge && (
                    <View style={styles.badge}>
                        <Text style={styles.badgeText} numberOfLines={1}>
                            {typeof badge === 'number' && badge > 99 ? '99+' : badge}
                        </Text>
                    </View>
                )}
            </View>
            <Text
                style={[styles.label, {color: tint, fontWeight: focused ? '800' : '600'}]}
                numberOfLines={2}
                allowFontScaling={false}
            >
                {label}
            </Text>
        </TouchableOpacity>
    );
});
PosTab.displayName = 'PosTab';

/**
 * Phone bottom bar for the POS hosts (take-away, tables, delivery). Replaces the
 * default react-navigation bar, whose half-height labels were truncated
 * ("Nyitott sza…") and whose order-type button floated over the second and third
 * tab. Here the button gets its own centre slot, so no tab is ever covered.
 *
 * Pass it as the navigator's `tabBar`; it reads icons, labels and badges from the
 * normal screen options, and hides itself when a screen sets
 * `tabBarStyle: {display: 'none'}` (keyboard up).
 *
 * @param {object} props react-navigation tab bar props (`state`, `descriptors`, `navigation`).
 * @param {React.ReactNode} [props.centerSlot] Rendered between the two halves (the order-type button).
 * @returns {React.ReactElement|null}
 */
const PosTabBar = ({state, descriptors, navigation, centerSlot}) => {
    const {theme, isDark} = useTheme();
    const bottomInset = useBottomInset();
    const focusedOptions = descriptors[state.routes[state.index]?.key]?.options;
    const flatStyle = StyleSheet.flatten(focusedOptions?.tabBarStyle) || {};

    if (flatStyle.display === 'none') {
        return null;
    }

    const colors = {
        primary: theme.colors.primary,
        subText: theme.colors.subText,
        // Soft chip behind the active icon; same warm tint family as the app's active tab.
        pill: isDark ? 'rgba(255,235,59,0.14)' : 'rgba(255,111,0,0.12)',
    };
    const {left, right, hasCenterSlot} = splitPosTabRoutes(state.routes);
    const renderTab = (route) => (
        <PosTab
            key={route.key}
            route={route}
            descriptor={descriptors[route.key]}
            focused={state.routes[state.index]?.key === route.key}
            navigation={navigation}
            colors={colors}
        />
    );

    return (
        <View
            style={[
                styles.bar,
                {
                    height: POS_TAB_BAR_HEIGHT + bottomInset,
                    paddingBottom: bottomInset,
                    backgroundColor: theme.colors.card,
                    borderTopColor: theme.colors.border,
                },
            ]}
        >
            {left.map(renderTab)}
            {hasCenterSlot && <View style={styles.centerSlot}>{centerSlot}</View>}
            {right.map(renderTab)}
        </View>
    );
};

const styles = StyleSheet.create({
    bar: {
        flexDirection: 'row',
        alignItems: 'center',
        borderTopWidth: StyleSheet.hairlineWidth,
    },
    tab: {
        flex: 1,
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 2,
    },
    iconPill: {
        width: 54,
        height: 30,
        borderRadius: 15,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 2,
    },
    label: {
        fontSize: 11.5,
        lineHeight: 13,
        textAlign: 'center',
    },
    centerSlot: {
        width: POS_FAB_SLOT_WIDTH,
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    badge: {
        position: 'absolute',
        top: -5,
        right: 0,
        minWidth: 18,
        height: 18,
        borderRadius: 9,
        paddingHorizontal: 4,
        backgroundColor: '#E53935',
        alignItems: 'center',
        justifyContent: 'center',
    },
    badgeText: {
        color: '#FFFFFF',
        fontSize: 10.5,
        fontWeight: '900',
        lineHeight: 13,
    },
});

export default PosTabBar;
